<script setup lang="ts">
import { useHomeStore } from "@/stores/HomeStore";

const integrations = [
  {
    name: "Binance",
    description: "World's largest crypto exchange",
    icon: "i-simple-icons-binance",
    status: "Live",
    color: "text-yellow-500",
  },
  {
    name: "Coinbase",
    description: "Trusted US exchange",
    icon: "i-simple-icons-coinbase",
    status: "Live",
    color: "text-blue-500",
  },
  {
    name: "Kraken",
    description: "Advanced trading platform",
    icon: "i-simple-icons-kraken",
    status: "Live",
    color: "text-purple-500",
  },
  {
    name: "Firi",
    description: "Norwegian crypto platform",
    icon: "i-heroicons-building-storefront",
    status: "Live",
    color: "text-green-500",
  },
];

const testimonials = [
  {
    content: "VimiCrypto has completely transformed how I manage my crypto portfolio. The real-time tracking is a game changer!",
    author: "Alex Chen",
    role: "Professional Trader",
    avatar: "👨‍💼",
  },
  {
    content: "Finally, a platform that handles multi-exchange tracking properly. The tax calculation feature alone is worth it.",
    author: "Sarah Johnson",
    role: "Crypto Investor",
    avatar: "👩‍💻",
  },
  {
    content: "The analytics and insights have helped me improve my trading strategy significantly. Highly recommended!",
    author: "Marcus Rodriguez",
    role: "Day Trader",
    avatar: "👨‍🚀",
  },
];

const homeStore = useHomeStore();
const signUp = () => {
  homeStore.signIn = false;
};
</script>

<template>
  <div class="relative py-24 bg-background sm:py-32">
    <div class="px-6 mx-auto max-w-7xl lg:px-8">
      <!-- Integrations Section -->
      <div class="max-w-4xl mx-auto mb-32">
        <div class="text-center">
          <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-secondary/10 text-secondary">
            Integrations
          </div>
          <h2 class="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Connect your favorite
            <span class="block mt-1 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              trading platforms
            </span>
          </h2>
          <p class="max-w-2xl mx-auto mt-6 text-lg text-muted-foreground">
            Seamlessly integrate with all major exchanges. More platforms added every month.
          </p>
        </div>

        <!-- Integration Grid -->
        <div class="grid grid-cols-2 gap-4 mt-12 sm:gap-6 lg:grid-cols-4">
          <div
            v-for="integration in integrations"
            :key="integration.name"
            class="relative p-6 transition-all duration-300 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg group">
            <div class="flex flex-col items-center text-center">
              <div
                class="flex items-center justify-center w-14 h-14 mb-4 rounded-xl bg-muted group-hover:scale-110 transition-transform duration-200">
                <UIcon
                  :name="integration.icon"
                  class="w-8 h-8"
                  :class="integration.color" />
              </div>
              <h3 class="text-base font-semibold text-foreground">
                {{ integration.name }}
              </h3>
              <p class="mt-1 text-xs text-muted-foreground">
                {{ integration.description }}
              </p>
              <div class="inline-flex items-center gap-1.5 px-2 py-1 mt-3 text-xs font-medium rounded-full bg-green-500/10 text-green-600">
                <div class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {{ integration.status }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Testimonials Section -->
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16">
          <div class="inline-flex items-center px-3 py-1 mb-6 text-sm font-medium rounded-full bg-primary/10 text-primary">
            Testimonials
          </div>
          <h2 class="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Loved by traders
            <span class="block mt-1 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              around the world
            </span>
          </h2>
        </div>

        <!-- Testimonials Grid -->
        <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div
            v-for="testimonial in testimonials"
            :key="testimonial.author"
            class="relative p-8 transition-all duration-300 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg">
            <!-- Quote Icon -->
            <div class="mb-4">
              <UIcon
                name="i-heroicons-chat-bubble-left-right"
                class="w-8 h-8 text-primary/40" />
            </div>

            <!-- Content -->
            <blockquote class="text-base leading-7 text-foreground">
              "{{ testimonial.content }}"
            </blockquote>

            <!-- Author -->
            <div class="flex items-center mt-6 gap-x-4">
              <div
                class="flex items-center justify-center w-12 h-12 text-2xl rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 border border-border">
                {{ testimonial.avatar }}
              </div>
              <div>
                <div class="font-semibold text-foreground">
                  {{ testimonial.author }}
                </div>
                <div class="text-sm text-muted-foreground">
                  {{ testimonial.role }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Final CTA -->
      <div class="max-w-4xl mx-auto mt-24">
        <div
          class="relative p-12 overflow-hidden text-center rounded-3xl bg-gradient-to-br from-card to-muted border border-border shadow-xl">
          <!-- Decorative Elements -->
          <div
            class="absolute top-0 left-0 w-40 h-40 rounded-full bg-primary/10 -translate-x-1/2 -translate-y-1/2 blur-2xl"
            aria-hidden="true" />
          <div
            class="absolute bottom-0 right-0 w-40 h-40 rounded-full bg-secondary/10 translate-x-1/2 translate-y-1/2 blur-2xl"
            aria-hidden="true" />

          <div class="relative z-10">
            <h2 class="text-3xl font-bold text-foreground sm:text-4xl">
              Start tracking your crypto today
            </h2>
            <p class="max-w-2xl mx-auto mt-4 text-lg text-muted-foreground">
              Join thousands of traders using VimiCrypto to make better trading decisions.
            </p>
            <div class="flex flex-col items-center justify-center gap-4 mt-8 sm:flex-row">
              <button
                @click="signUp"
                class="px-8 py-4 text-base font-semibold transition-all duration-200 rounded-lg shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                Start Free Trial
              </button>
              <a
                href="#features"
                class="text-base font-semibold transition-colors duration-200 text-secondary hover:text-secondary/80">
                Learn more →
              </a>
            </div>
            <p class="mt-6 text-sm text-muted-foreground">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
